import { Controller } from '@hotwired/stimulus';
import { characterLayout } from '../../lib/character_layout.js';

// Move only canonical cards; future-round copies remain in the scrolling rail.
export default class extends Controller {
  static values = { characterLayout: { type: String, default: 'top' }, combat: Boolean };

  connect() {
    this.boundsElement = this.element.parentElement;
    this.cards = new Set();
    this.observer = new MutationObserver(records => {
      const source = this.source;
      if (records.some(record => record.target === this.element ||
          (record.type === 'childList' && record.target === source) ||
          (record.type === 'attributes' && record.target.matches('[data-character-list-2-card]')))) this.schedule();
    });
    this.observer.observe(this.element, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'data-position'] });
    this.layoutSettled = event => {
      if (event.target === this.screen || event.target === this.element ||
          event.target.classList.contains('character-list-2__viewport')) this.schedule();
    };
    this.screen.addEventListener('transitionend', this.layoutSettled);
    this.resizeObserver = new ResizeObserver(() => this.schedule());
    this.resizeObserver.observe(this.element);
    this.resizeObserver.observe(this.screen);
    this.schedule();
  }

  // Read the available content bounds; all mutations stay inside this component.
  get screen() { return this.boundsElement || this.element.parentElement; }

  get source() { return this.element.querySelector('[data-game-screen--character-list-2-target="source"]'); }

  get quarterTurn() {
    return this.element.classList.contains('character-list-2--rotation-ninety') ||
      this.element.classList.contains('character-list-2--rotation-two-seventy');
  }

  characterLayoutValueChanged() { this.schedule(); }
  combatValueChanged() {
    this.dispatch('combat-changed');
    this.schedule();
  }

  schedule() {
    if (this.frame !== undefined) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = undefined;
      this.render();
    });
  }

  preservePosition(event) {
    if ((this.cards?.has(event.target) && event.detail.attributeName === 'style') ||
        (event.target === this.element && event.detail.attributeName === 'data-character-list-2-positioned') ||
        (event.target.matches?.('.character-list-2__viewport') && event.detail.attributeName === 'data-character-list2-scroll-left') ||
        (this.element.dataset['characterList-2Positioned'] !== undefined &&
          event.target.matches?.('.character-list-2__track') && event.detail.attributeName === 'style')) {
      event.preventDefault();
    }
  }

  render() {
    if (!this.element.isConnected) return;
    const characters = [...this.source?.querySelectorAll(':scope > [data-character-list-2-card]:not(.is-hidden)') || []]
      .sort((a, b) => Number(a.dataset.position) - Number(b.dataset.position));
    const entries = characters.map(character => ({ character, card: character.querySelector('.character-list-2__card') }));
    const requestedFocus = characters.find(character => character.classList.contains('is-focused'));
    // Keep the latest request in the DOM while the previous focus returns home.
    // Decide before positioning any card, regardless of its order in the roster.
    const waitingForReturn = entries.some(({ character, card }) => {
      return card.style.zIndex === '20' &&
        (character !== requestedFocus || this.returningCards?.has(card));
    });
    const focus = waitingForReturn ? undefined : requestedFocus;
    if (requestedFocus || (!this.combatValue && this.characterLayoutValue !== 'top')) {
      clearTimeout(this.restoreOverflow);
      this.restoreOverflow = undefined;
      this.setPositioned(true);
    } else if (this.element.dataset['characterList-2Positioned'] !== undefined && this.restoreOverflow === undefined) {
      // Keep returning cards visible until they reach the scrollable top roster.
      this.restoreOverflow = setTimeout(() => {
        this.restoreOverflow = undefined;
        this.setPositioned(false);
      }, 325);
    }
    const width = this.screen.clientWidth;
    const height = this.screen.clientHeight;
    const quarterTurn = this.quarterTurn;
    const context = { width, height, rotations: new Map() };
    const positions = characterLayout(this.characterLayoutValue, characters.length, width, height);
    const current = new Set();
    entries.forEach(({ character, card }, index) => {
      current.add(card);
      if (!this.cards?.has(card)) this.resizeObserver?.observe(card);
      if ((this.combatValue || this.characterLayoutValue === 'top') && character !== focus) {
        this.resetPosition(card);
        this.updateStacking(card, false, false);
        return;
      }
      let focusWidth = quarterTurn
        ? Math.min(height * 0.6, width * 0.9 * 2 / 3)
        : Math.min(height * 0.6 * 2 / 3, width * 0.9);
      if (character.classList.contains('has-conditions') || character.classList.contains('is-active')) {
        focusWidth = Math.min(focusWidth, width / (quarterTurn ? 4.7 : 3.15));
      }
      const box = character === focus
        ? { x: width / 2, y: height / 2, width: focusWidth }
        : { ...positions[index], width: positions[index].width * (quarterTurn ? 2 / 3 : 1) };
      this.positionCard(card, box, context);
      this.updateStacking(card, character === focus, true);
    });
    for (const card of this.cards || []) {
      if (current.has(card)) continue;
      this.resizeObserver?.unobserve(card);
      this.resetCard(card);
    }
    this.cards = current;
    this.dispatch('positioned');
  }

  positionCard(card, box, context = {}) {
    const cardWidth = card.offsetWidth;
    if (!cardWidth) return;
    const computed = getComputedStyle(card);
    const current = card.getBoundingClientRect();
    const screen = context.rect ||= this.screen.getBoundingClientRect();
    const screenAngle = context.angle ??= this.rotation(this.screen, context.rotations);
    const angle = this.rotation(card.parentElement, context.rotations);
    const [translateX = 0, translateY = 0] = computed.translate === 'none'
      ? [] : computed.translate.split(' ').map(parseFloat);
    // Scale and rotation use the card center as origin. Subtract its current
    // translated center mathematically instead of resetting scale and forcing
    // another layout, which used to restart focus motion on every Turbo morph.
    const originX = current.left + current.width / 2 - translateX * Math.cos(angle) + translateY * Math.sin(angle);
    const originY = current.top + current.height / 2 - translateX * Math.sin(angle) - translateY * Math.cos(angle);
    const x = box.x - (context.width ?? this.screen.clientWidth) / 2;
    const y = box.y - (context.height ?? this.screen.clientHeight) / 2;
    const dx = screen.left + screen.width / 2 + x * Math.cos(screenAngle) - y * Math.sin(screenAngle) - originX;
    const dy = screen.top + screen.height / 2 + x * Math.sin(screenAngle) + y * Math.cos(screenAngle) - originY;
    const desiredX = dx * Math.cos(angle) + dy * Math.sin(angle);
    const desiredY = -dx * Math.sin(angle) + dy * Math.cos(angle);
    const [previousX, previousY] = (card.style.translate || '').split(' ').map(parseFloat);
    if (!Number.isFinite(previousX) || Math.abs(previousX - desiredX) > 0.1 || Math.abs(previousY - desiredY) > 0.1) {
      card.style.translate = `${desiredX}px ${desiredY}px`;
    }
    const scale = box.width / cardWidth;
    if (!Number.isFinite(parseFloat(card.style.scale)) || Math.abs(parseFloat(card.style.scale) - scale) > 0.0001) {
      card.style.scale = String(scale);
    }
  }

  updateStacking(card, focused, positioned) {
    this.returningCards ||= new Map();
    const zIndex = focused ? '20' : positioned ? '10' : '';
    if (focused || card.style.zIndex !== '20') {
      this.returningCards.delete(card);
      if (card.style.zIndex !== zIndex) card.style.zIndex = zIndex;
      return;
    }

    // Read after positioning so the browser starts the new CSS transitions.
    const animations = card.getAnimations().filter(animation =>
      ['translate', 'scale'].includes(animation.transitionProperty));
    if (!animations.length) {
      this.returningCards.delete(card);
      card.style.zIndex = zIndex;
      this.schedule();
      return;
    }

    const previous = this.returningCards.get(card);
    if (previous?.zIndex === zIndex && previous.animations.length === animations.length &&
        animations.every((animation, index) => animation === previous.animations[index])) return previous.promise;
    const returning = { animations, zIndex };
    this.returningCards.set(card, returning);
    returning.promise = Promise.allSettled(animations.map(animation => animation.finished)).then(() => {
      // A morph, refocus, or removal can supersede this return animation.
      if (this.returningCards.get(card) !== returning) return;
      this.returningCards.delete(card);
      card.style.zIndex = zIndex;
      this.schedule();
    });
    return returning.promise;
  }

  rotation(element, cache = new Map()) {
    if (cache.has(element)) return cache.get(element);
    let angle = 0;
    const ancestors = [];
    for (let current = element; current; current = current.parentElement) {
      if (cache.has(current)) {
        angle = cache.get(current);
        break;
      }
      ancestors.push(current);
    }
    for (const ancestor of ancestors.reverse()) {
      const transform = getComputedStyle(ancestor).transform;
      if (transform !== 'none') {
        const matrix = new DOMMatrixReadOnly(transform);
        angle += Math.atan2(matrix.b, matrix.a);
      }
      cache.set(ancestor, angle);
    }
    return angle;
  }

  resetPosition(card) {
    for (const property of ['translate', 'scale', 'transition']) {
      if (card.style.getPropertyValue(property)) card.style.removeProperty(property);
    }
  }

  resetCard(card) {
    this.returningCards?.delete(card);
    this.resetPosition(card);
    card.style.removeProperty('z-index');
  }

  setPositioned(positioned) {
    const wasPositioned = this.element.dataset['characterList-2Positioned'] !== undefined;
    if (positioned === wasPositioned) return;
    const viewport = this.element.querySelector('.character-list-2__viewport');
    const track = this.element.querySelector('.character-list-2__track');
    if (positioned) {
      // overflow:visible cannot retain scrollLeft. Keep the same visual origin
      // while the original card travels outside the rail to its focus position.
      const left = viewport.scrollLeft;
      viewport.dataset.characterList2ScrollLeft = String(left);
      track.style.translate = `${-left}px 0`;
      this.element.dataset['characterList-2Positioned'] = '';
    } else {
      const left = Number(viewport.dataset.characterList2ScrollLeft || 0);
      delete this.element.dataset['characterList-2Positioned'];
      delete viewport.dataset.characterList2ScrollLeft;
      track.style.removeProperty('translate');
      viewport.scrollLeft = left;
    }
    this.dispatch('mode-changed');
  }

  clear() {
    cancelAnimationFrame(this.frame);
    this.frame = undefined;
    clearTimeout(this.restoreOverflow);
    this.restoreOverflow = undefined;
    this.setPositioned(false);
    for (const card of this.cards || []) this.resetCard(card);
    this.cards = new Set();
  }

  disconnect() {
    this.screen.removeEventListener('transitionend', this.layoutSettled);
    this.observer.disconnect();
    this.resizeObserver.disconnect();
    this.clear();
  }
}
